import React, { useEffect, useMemo, useRef, useState } from 'react';
import { parseUs, formatUs, phoenixTodayIso, isoAddDays, isoAddMonths, isoWeekday, daysInMonth, firstOfMonth, inCalendarRange, MONTH_NAMES } from './coverageFields';

/**
 * BUILD-043 - a date box you can TYPE in (MM/DD/YYYY, validated by the caller exactly as before) with a calendar and an explicit "Today" shortcut beside it.
 *
 * Rules (Owner usability request, no change to any business rule):
 *  - The box keeps the text the person typed; this component never "fixes" or guesses a malformed date. The calendar only ever writes a REAL date the person clicked, in MM/DD/YYYY.
 *  - Opening the calendar changes nothing. It opens on the month of the date in the box, or - when the box is empty or not a real date - on today's month in Phoenix. Nothing is filled
 *    or saved until someone clicks a day or the Today button. Closing (Esc, clicking away, Close) leaves the box exactly as it was.
 *  - "Today" fills the Phoenix calendar date (the company date), as an explicit click.
 *  - There was no calendar component in the app (other screens use the browser's own date control, which shows the browser's format and cannot open on the Phoenix date without filling
 *    it), so this small one reuses the existing MM/DD/YYYY helpers and validation of the Documents & compliance form.
 */
export function DateField(props: {
  id: string; testId: string; label: string; value: string; onChange: (text: string) => void;
  /** Phoenix "today" as YYYY-MM-DD (the server's date); falls back to the device clock converted to Phoenix time. */
  today?: string; disabled?: boolean; invalid?: boolean; placeholder?: string; inputClassName?: string;
}) {
  const today = props.today && /^\d{4}-\d{2}-\d{2}$/.test(props.today) ? props.today : phoenixTodayIso();
  const [open, setOpen] = useState(false);
  const selected = parseUs(props.value);                              // null when empty OR not a real date: the calendar never guesses
  const [cursor, setCursor] = useState<string>(selected || today);
  const wrap = useRef<HTMLDivElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const grid = useRef<HTMLDivElement | null>(null);

  const openCalendar = () => { setCursor(selected && inCalendarRange(selected) ? selected : today); setOpen(true); };
  const close = (refocus: boolean) => { setOpen(false); if (refocus) setTimeout(() => input.current?.focus(), 0); };
  const pick = (iso: string) => { props.onChange(formatUs(iso)); close(true); };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  // keep keyboard focus on the highlighted day as it moves
  useEffect(() => {
    if (!open) return;
    grid.current?.querySelector<HTMLElement>(`[data-iso="${cursor}"]`)?.focus();
  }, [open, cursor]);

  const { y, m } = { y: Number(cursor.slice(0, 4)), m: Number(cursor.slice(5, 7)) };
  const cells = useMemo(() => {
    const first = firstOfMonth(cursor); const lead = isoWeekday(first); const n = daysInMonth(y, m);
    const out: Array<string | null> = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= n; d++) out.push(`${first.slice(0, 8)}${String(d).padStart(2, '0')}`);
    while (out.length % 7) out.push(null);
    return out;
  }, [cursor, y, m]);

  const move = (next: string) => { if (inCalendarRange(next)) setCursor(next); };
  const onGridKey = (e: React.KeyboardEvent) => {
    const k = e.key;
    const delta: Record<string, () => string> = {
      ArrowLeft: () => isoAddDays(cursor, -1), ArrowRight: () => isoAddDays(cursor, 1), ArrowUp: () => isoAddDays(cursor, -7), ArrowDown: () => isoAddDays(cursor, 7),
      PageUp: () => isoAddMonths(cursor, -1), PageDown: () => isoAddMonths(cursor, 1), Home: () => firstOfMonth(cursor), End: () => isoAddDays(isoAddMonths(firstOfMonth(cursor), 1), -1),
    };
    if (delta[k]) { e.preventDefault(); move(delta[k]()); }
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && open) { e.preventDefault(); e.nativeEvent.stopPropagation(); close(true); }   // closes the calendar only, not the drawer behind it
  };
  const btn = 'px-2 py-1.5 border rounded text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50';
  return (
    <div className="relative" ref={wrap} onKeyDown={onKeyDown}>
      <div className="flex items-center gap-1">
        <input ref={input} id={props.id} data-testid={props.testId} value={props.value} placeholder={props.placeholder || 'MM/DD/YYYY'} inputMode="numeric" disabled={props.disabled} aria-invalid={props.invalid || undefined}
          onChange={(e) => props.onChange(e.target.value)} className={props.inputClassName || 'w-full px-2 py-1.5 border rounded text-sm'} />
        <button type="button" data-testid={`${props.testId}-calendar`} aria-label={`Choose ${props.label} from a calendar`} aria-haspopup="dialog" aria-expanded={open} disabled={props.disabled}
          onClick={() => (open ? close(false) : openCalendar())} className={btn}><span aria-hidden="true">&#128197;</span></button>
        <button type="button" data-testid={`${props.testId}-today`} aria-label={`Set ${props.label} to today`} disabled={props.disabled} onClick={() => props.onChange(formatUs(today))} className={btn}>Today</button>
      </div>
      {open && (
        <div role="dialog" aria-label={`${props.label} calendar`} data-testid={`${props.testId}-popover`} className="absolute left-0 z-30 mt-1 w-64 bg-white border rounded-lg shadow-lg p-2">
          <div className="flex items-center justify-between mb-1">
            <button type="button" data-testid={`${props.testId}-prev`} aria-label="Previous month" disabled={!inCalendarRange(isoAddMonths(cursor, -1))} onClick={() => move(isoAddMonths(cursor, -1))} className={btn}>&lsaquo;</button>
            <div className="text-sm font-medium" aria-live="polite" data-testid={`${props.testId}-month`}>{MONTH_NAMES[m - 1]} {y}</div>
            <button type="button" data-testid={`${props.testId}-next`} aria-label="Next month" disabled={!inCalendarRange(isoAddMonths(cursor, 1))} onClick={() => move(isoAddMonths(cursor, 1))} className={btn}>&rsaquo;</button>
          </div>
          <div className="grid grid-cols-7 text-center text-[11px] text-gray-500 mb-0.5" aria-hidden="true">{['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => <div key={d}>{d}</div>)}</div>
          <div ref={grid} role="group" aria-label={`${MONTH_NAMES[m - 1]} ${y}`} className="grid grid-cols-7 gap-0.5" onKeyDown={onGridKey}>
            {cells.map((iso, i) => iso === null ? <div key={`b${i}`} /> : (
              <button key={iso} type="button" data-iso={iso} data-testid={`${props.testId}-day-${iso}`} tabIndex={iso === cursor ? 0 : -1} aria-pressed={iso === selected}
                aria-label={`${MONTH_NAMES[Number(iso.slice(5, 7)) - 1]} ${Number(iso.slice(8, 10))}, ${iso.slice(0, 4)}${iso === today ? ' (today)' : ''}`}
                onClick={() => pick(iso)}
                className={`h-8 text-xs rounded ${iso === selected ? 'bg-blue text-white' : iso === today ? 'border border-blue text-blue' : 'hover:bg-gray-100'}`}>{Number(iso.slice(8, 10))}</button>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <button type="button" data-testid={`${props.testId}-popover-today`} onClick={() => pick(today)} className={btn}>Today</button>
            <button type="button" data-testid={`${props.testId}-close`} onClick={() => close(true)} className={btn}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
